/**
 * services/web-ui/src/components/audit/TrackView.tsx
 *
 * Hand-rolled SVG timeline — no chart library.
 *
 * Layout (top→bottom within the SVG):
 *   Row 1 (y=0..STAGE_H):     Stage blocks — width proportional to duration
 *   Row 2 (y=STAGE_H..MID_H): Event markers — tool calls (dots), policy (diamonds),
 *                               tripwires (lightning bolts)
 *   Row 3 (y=MID_H..CONF_H):  Confidence track — line graph from confidence.aggregate
 *                               audit events. Empty if no such events exist.
 *
 * Pixel mapping: linear. x = (ts_ms - startTs) / totalDurationMs * WIDTH
 * Selection overlay: translucent rectangle covering selected time range.
 */

import { useRef, useLayoutEffect, useState } from "react";
import type { WorkflowDefinition } from "@vault/types";
import type { SessionEvent } from "../../api/voice-sessions";
import { getStageColors, getStageInfo } from "../../lib/stage-descriptions";
import { StageTooltip } from "./StageTooltip";

const STAGE_H = 36;
const MARKER_BAND_H = 28;
const CONF_TRACK_H = 40;
const TOTAL_H = STAGE_H + MARKER_BAND_H + CONF_TRACK_H;
const STAGE_Y = 0;
const MARKER_Y = STAGE_H + 4;
const CONF_Y = STAGE_H + MARKER_BAND_H + 2;

export interface TrackSelection {
  ts_start_ms: number;
  ts_end_ms: number;
  source: "track";
  /** Populated when clicking a stage block, so FlowchartView can highlight the node */
  stage?: string;
}

interface Props {
  events: SessionEvent[];
  workflow: WorkflowDefinition | null;
  totalDurationMs: number;
  selection: { ts_start_ms: number; ts_end_ms: number } | null;
  onSelect(range: TrackSelection | null): void;
  startedAtMs: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function tsToX(ts: number, startMs: number, totalMs: number, width: number): number {
  if (totalMs <= 0) return 0;
  return ((ts - startMs) / totalMs) * width;
}

/** Derive stage segments from stage.entered events */
interface StageSegment {
  stage: string;
  startMs: number;
  endMs: number;
  durationMs: number;
}

function buildStageSegments(events: SessionEvent[], totalEndMs: number): StageSegment[] {
  const entries = events
    .filter((e) => e.type === "stage.entered")
    .sort((a, b) => a.ts_ms - b.ts_ms);

  const segments: StageSegment[] = [];

  for (let i = 0; i < entries.length; i++) {
    const ev = entries[i]!;
    const stage =
      typeof ev.payload?.["stage"] === "string"
        ? (ev.payload["stage"] as string)
        : ev.stage ?? "UNKNOWN";
    const startMs = ev.ts_ms;
    const endMs = i + 1 < entries.length ? entries[i + 1]!.ts_ms : totalEndMs;
    segments.push({ stage, startMs, endMs, durationMs: endMs - startMs });
  }

  return segments;
}

/** Confidence aggregate points from audit events */
interface ConfPoint {
  ts_ms: number;
  overall: number;
}

function buildConfPoints(events: SessionEvent[]): ConfPoint[] {
  return events
    .filter((e) => e.type === "confidence.aggregate")
    .map((e) => ({
      ts_ms: e.ts_ms,
      overall:
        typeof e.payload?.["overall"] === "number" ? (e.payload["overall"] as number) : 0,
    }))
    .sort((a, b) => a.ts_ms - b.ts_ms);
}

function pointsToPolyline(
  points: ConfPoint[],
  startMs: number,
  totalMs: number,
  width: number,
): string {
  return points
    .map((p) => {
      const x = tsToX(p.ts_ms, startMs, totalMs, width);
      const y = CONF_Y + CONF_TRACK_H - p.overall * CONF_TRACK_H;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

// ─── Marker shapes ────────────────────────────────────────────────────────────

function ToolDot({ x, title }: { x: number; title: string }) {
  const cy = MARKER_Y + MARKER_BAND_H / 2;
  return (
    <circle cx={x} cy={cy} r={4} fill="#6b7280" opacity={0.8}>
      <title>{title}</title>
    </circle>
  );
}

function PolicyDiamond({ x, decision, title }: { x: number; decision: string; title: string }) {
  const cy = MARKER_Y + MARKER_BAND_H / 2;
  const s = 6;
  const fill =
    decision === "allow" ? "#22c55e" : decision === "override" ? "#eab308" : "#ef4444";
  const pts = `${x},${cy - s} ${x + s},${cy} ${x},${cy + s} ${x - s},${cy}`;
  return (
    <polygon points={pts} fill={fill} opacity={0.9}>
      <title>{title}</title>
    </polygon>
  );
}

function TripwireBolt({ x, title }: { x: number; title: string }) {
  // Simple lightning bolt shape
  const cy = MARKER_Y + MARKER_BAND_H / 2;
  const pts = `${x - 2},${cy - 7} ${x + 3},${cy - 1} ${x},${cy - 1} ${x + 4},${cy + 7} ${x - 2},${cy} ${x + 1},${cy}`;
  return (
    <polygon points={pts} fill="#ef4444" stroke="#dc2626" strokeWidth={0.5} opacity={0.9}>
      <title>{title}</title>
    </polygon>
  );
}

// ─── TrackView ────────────────────────────────────────────────────────────────

export function TrackView({
  events,
  workflow,
  totalDurationMs,
  selection,
  onSelect,
  startedAtMs,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);

  // Measure container width for responsive SVG
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setWidth(w);
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const safeTotal = Math.max(totalDurationMs, 1);
  const endMs = startedAtMs + safeTotal;

  const segments = buildStageSegments(events, endMs);
  const confPoints = buildConfPoints(events);
  const polyline = confPoints.length >= 2 ? pointsToPolyline(confPoints, startedAtMs, safeTotal, width) : null;

  // Compute skipped stage placeholders from workflow.required_stages
  const requiredStages = workflow?.required_stages ?? [];
  const visitedSet = new Set(segments.map((s) => s.stage));
  const skippedPlaceholders = requiredStages.filter((s) => !visitedSet.has(s));

  // Markers
  const toolEvents = events.filter((e) => e.type === "tool.call");
  const policyEvents = events.filter(
    (e) => e.type === "policy.denied" || e.type === "policy.check" || e.type === "policy.override_required",
  );
  const tripwireEvents = events.filter((e) => e.type.startsWith("stt.tripwire") || e.type.startsWith("tripwire."));

  // Selection rectangle
  const selX1 = selection
    ? tsToX(selection.ts_start_ms, startedAtMs, safeTotal, width)
    : null;
  const selX2 = selection
    ? tsToX(selection.ts_end_ms, startedAtMs, safeTotal, width)
    : null;

  const handleStageClick = (seg: StageSegment) => {
    onSelect({ ts_start_ms: seg.startMs, ts_end_ms: seg.endMs, source: "track", stage: seg.stage });
  };

  return (
    <div ref={containerRef} className="w-full">
      <svg
        width={width}
        height={TOTAL_H + 8}
        className="block"
        style={{ fontFamily: "monospace", fontSize: 10 }}
      >
        {/* ── Stage blocks ─────────────────────────────────────────────── */}
        {segments.map((seg, i) => {
          const x = tsToX(seg.startMs, startedAtMs, safeTotal, width);
          const w = Math.max(tsToX(seg.endMs, startedAtMs, safeTotal, width) - x, 2);
          const colors = getStageColors(seg.stage);
          const isSelected =
            selection &&
            seg.startMs < selection.ts_end_ms &&
            seg.endMs > selection.ts_start_ms;

          return (
            <StageTooltip key={i} stage={seg.stage} events={events} durationMs={seg.durationMs}>
              <g
                onClick={() => handleStageClick(seg)}
                style={{ cursor: "pointer" }}
              >
                <rect
                  x={x + 0.5}
                  y={STAGE_Y + 1}
                  width={w - 1}
                  height={STAGE_H - 2}
                  rx={3}
                  fill={colors.hex}
                  fillOpacity={isSelected ? 0.85 : 0.55}
                  stroke={colors.hex}
                  strokeOpacity={0.8}
                  strokeWidth={isSelected ? 1.5 : 0.5}
                />
                {w > 32 && (
                  <text
                    x={x + w / 2}
                    y={STAGE_Y + STAGE_H / 2 + 4}
                    textAnchor="middle"
                    fill="white"
                    fillOpacity={0.9}
                    fontSize={9}
                  >
                    {seg.stage.length > 12 ? seg.stage.slice(0, 11) + "…" : seg.stage}
                  </text>
                )}
              </g>
            </StageTooltip>
          );
        })}

        {/* ── Skipped required stage placeholders ──────────────────────── */}
        {skippedPlaceholders.map((stage, i) => {
          // Render a dashed gray box at the right edge for each unvisited required stage
          const boxW = Math.min(60, width / (skippedPlaceholders.length + 1));
          const x = width - (skippedPlaceholders.length - i) * (boxW + 4) - 4;
          const info = getStageInfo(stage);
          return (
            <g key={`skip-${i}`} opacity={0.5}>
              <rect
                x={x}
                y={STAGE_Y + 1}
                width={boxW}
                height={STAGE_H - 2}
                rx={3}
                fill="none"
                stroke="#6b7280"
                strokeWidth={1}
                strokeDasharray="4,3"
              />
              <text
                x={x + boxW / 2}
                y={STAGE_Y + STAGE_H / 2 + 4}
                textAnchor="middle"
                fill="#9ca3af"
                fontSize={8}
              >
                {stage.slice(0, 8)}
              </text>
              <title>SKIPPED (required): {stage} — {info.description}</title>
            </g>
          );
        })}

        {/* ── Marker band background ────────────────────────────────────── */}
        <rect
          x={0}
          y={STAGE_H}
          width={width}
          height={MARKER_BAND_H}
          fill="#111827"
          fillOpacity={0.6}
        />

        {/* ── Event markers ────────────────────────────────────────────── */}
        {toolEvents.map((ev, i) => {
          const x = tsToX(ev.ts_ms, startedAtMs, safeTotal, width);
          const toolName = typeof ev.payload?.["tool"] === "string"
            ? ev.payload["tool"] as string
            : ev.tool ?? "tool";
          return <ToolDot key={`tool-${i}`} x={x} title={`${toolName} (${ev.result_status})`} />;
        })}

        {policyEvents.map((ev, i) => {
          const x = tsToX(ev.ts_ms, startedAtMs, safeTotal, width);
          const decision =
            ev.type === "policy.denied"
              ? "deny"
              : ev.type === "policy.override_required"
                ? "override"
                : "allow";
          const action = typeof ev.payload?.["action"] === "string"
            ? ev.payload["action"] as string
            : ev.tool ?? "policy";
          return (
            <PolicyDiamond
              key={`policy-${i}`}
              x={x}
              decision={decision}
              title={`${ev.type}: ${action}`}
            />
          );
        })}

        {tripwireEvents.map((ev, i) => {
          const x = tsToX(ev.ts_ms, startedAtMs, safeTotal, width);
          return <TripwireBolt key={`tw-${i}`} x={x} title={`Tripwire: ${ev.type}`} />;
        })}

        {/* ── Confidence track ─────────────────────────────────────────── */}
        <rect
          x={0}
          y={STAGE_H + MARKER_BAND_H}
          width={width}
          height={CONF_TRACK_H + 8}
          fill="#0f172a"
          fillOpacity={0.5}
        />
        {/* Baseline at 0.5 */}
        <line
          x1={0} y1={CONF_Y + CONF_TRACK_H / 2}
          x2={width} y2={CONF_Y + CONF_TRACK_H / 2}
          stroke="#374151"
          strokeWidth={0.5}
          strokeDasharray="4,4"
        />
        {polyline && (
          <polyline
            points={polyline}
            fill="none"
            stroke="#38bdf8"
            strokeWidth={1.5}
            strokeLinejoin="round"
            opacity={0.8}
          />
        )}
        {!polyline && (
          <text
            x={width / 2}
            y={CONF_Y + CONF_TRACK_H / 2 + 4}
            textAnchor="middle"
            fill="#374151"
            fontSize={9}
          >
            no confidence data
          </text>
        )}

        {/* ── Selection overlay ─────────────────────────────────────────── */}
        {selX1 !== null && selX2 !== null && (
          <rect
            x={Math.min(selX1, selX2)}
            y={0}
            width={Math.max(Math.abs(selX2 - selX1), 2)}
            height={TOTAL_H + 8}
            fill="#f59e0b"
            fillOpacity={0.08}
            stroke="#f59e0b"
            strokeOpacity={0.3}
            strokeWidth={1}
            pointerEvents="none"
          />
        )}

        {/* ── Legend ───────────────────────────────────────────────────── */}
        <g transform={`translate(4, ${TOTAL_H + 2})`} fill="#6b7280" fontSize={8}>
          <circle cx={4} cy={0} r={3} fill="#6b7280" />
          <text x={10} y={3}>tool</text>
          <polygon points="22,0 28,-5 34,0 28,5" fill="#22c55e" />
          <text x={37} y={3}>allow</text>
          <polygon points="56,0 62,-5 68,0 62,5" fill="#ef4444" />
          <text x={71} y={3}>deny</text>
          <polygon points="88,-6 86,0 92,-1 84,6 88,0 82,1" fill="#ef4444" />
          <text x={96} y={3}>tripwire</text>
        </g>
      </svg>
    </div>
  );
}
